import { useShowContext } from 'framework/show';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  Milestone,
  MILESTONE_STATUS,
  MILESTONE_STATUS_OPTIONS,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { MilestonesType } from './types';
import { useMemo, useRef, useState } from 'react';
import { FormActionType } from 'framework/ui/menu';
import {
  Box,
  Breakpoint,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import ArrowDownIcon from '@mui/icons-material/ArrowDropDownOutlined';
import { EndDateIcon, OPDateIcon } from 'framework/ui/mui-builder';
import { useDateService } from 'framework/utils';
import { useListContext } from 'framework/list';
import useMilestonesService from './useMilestonesService';
import { DateInput, ModalForm, TextInput } from 'framework/form';
import { isProtectedMilestone } from './utils/milestoneProtection';
import { useProjectOutcomes } from '../businessOutcomes/hooks/useOutcomes';

interface ModalActionButton {
  title: string;
  disabled?: boolean;
}

interface ModalActionType {
  form: FormActionType;
  menu: ModalActionButton;
}

const MilestonesModalsActionButton = ({
  projectId,
  onClick,
}: {
  projectId: string;
  onClick: (formView: FormActionType) => void;
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const { data } = useShowContext<MilestonesType>();
  const milestone = data.getData();
  const { isAccessGranted } = useProjectSecurityContext();
  const { refetch } = useProjectOutcomes(projectId);
  const { getToday } = useDateService();
  const { updateInList, data: listData } = useListContext();
  const {
    completeMilestone,
    rejectMilestone,
    milestonesBatch,
    getCompleteMilestoneValidationSchema,
    getRejectMilestoneValidationSchema,
  } = useMilestonesService();

  const canManage = useMemo(
    () => isAccessGranted(ACCESS_INFO_SECTIONS.MILESTONE, ACCESS_INFO_ACTIONS.MANAGE),
    [isAccessGranted],
  );
  const isActive = milestone.status === MILESTONE_STATUS.ACTIVE;
  const isProtected = isProtectedMilestone(milestone);

  const getUpdatedMilestone = (response: Milestone) => {
    const currentMilestone = (listData?.items || []).find(item => item.id === milestone.id) || milestone;

    return {
      ...currentMilestone,
      ...response,
      businessOutcomes: currentMilestone.businessOutcomes,
    };
  };

  if (!isActive) {
    return (
      <Typography color={milestone.status === MILESTONE_STATUS.COMPLETED ? 'success' : 'error'}>
        {MILESTONE_STATUS_OPTIONS[milestone.status as keyof typeof MILESTONE_STATUS_OPTIONS]}
      </Typography>
    );
  }

  if (!canManage) {
    return null;
  }

  const config: ModalActionType[] = [
    {
      menu: {
        title: 'Complete',
      },
      form: {
        modalSize: 'smallMobile' as Breakpoint,
        title: milestone.initialPlannedDate
          ? 'Please, enter the milestone completion date'
          : 'Please, enter the milestone dates',
        Component: (props: any) => {
          const hasInitialPlannedDate = !!milestone.initialPlannedDate;
          return (
            <ModalForm
              {...props}
              initialData={{ hasInitialPlannedDate }}
              submitFormFunc={async (values: any) => {
                if (!hasInitialPlannedDate) {
                  const allMilestones = (listData?.items || []) as MilestonesType[];
                  const updatedMilestones = allMilestones.map(m =>
                    m.id === milestone.id ? { ...m, initialPlannedDate: values.initialPlannedDate } : m,
                  );
                  await milestonesBatch(projectId, updatedMilestones);
                }
                return completeMilestone(milestone.id, { completionDate: values.completionDate });
              }}
              afterSubmit={{
                execute: async response => {
                  updateInList?.(getUpdatedMilestone(response));
                  await refetch();
                },
              }}
              getValidationSchemaFunc={getCompleteMilestoneValidationSchema}
            >
              {!hasInitialPlannedDate && (
                <DateInput
                  source="initialPlannedDate"
                  label="Original planned date"
                  dateIcon={
                    <OPDateIcon
                      sx={{
                        position: 'absolute',
                        left: 10,
                        top: 10,
                        opacity: 0.8,
                        fontSize: '1.2rem',
                        zIndex: 1,
                      }}
                    />
                  }
                />
              )}
              <DateInput
                source="completionDate"
                maxDate={getToday()}
                dateIcon={
                  <EndDateIcon
                    sx={{
                      position: 'absolute',
                      left: 10,
                      top: 10,
                      opacity: 0.8,
                      fontSize: '1.2rem',
                      zIndex: 1,
                    }}
                  />
                }
              />
            </ModalForm>
          );
        },
      },
    },
    ...(isProtected
      ? ([] as ModalActionType[])
      : [
          {
            menu: {
              title: 'Reject',
            },
            form: {
              modalSize: 'smallMobile' as Breakpoint,
              title: 'Are you sure?',
              Component: (props: any) => {
                return (
                  <ModalForm
                    {...props}
                    submitFormFunc={values => rejectMilestone(milestone.id, values)}
                    afterSubmit={{
                      execute: async response => {
                        updateInList?.(getUpdatedMilestone(response));
                        await refetch();
                      },
                    }}
                    getValidationSchemaFunc={getRejectMilestoneValidationSchema}
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography mb="10px">Reject this milestone? This action cannot be undone.</Typography>

                      <TextInput label="Reason" source="rejectionReason" />
                    </Box>
                  </ModalForm>
                );
              },
            },
          },
        ]),
  ];

  if (config.length === 0) {
    return null;
  }

  const mainAction = config[0];
  const otherActions = config.slice(1);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleMenuItemClick = (form: FormActionType) => {
    onClick(form);
  };

  return (
    <>
      <ButtonGroup variant="contained" ref={anchorRef}>
        <Button onClick={() => handleMenuItemClick(mainAction.form)} disabled={mainAction.menu.disabled}>
          {mainAction.menu.title}
        </Button>
        {otherActions.length > 0 && (
          <Button onClick={handleToggle} sx={{ minWidth: '25px !important', p: '0px !important' }}>
            <ArrowDownIcon />
          </Button>
        )}
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1300 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-start"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper
              sx={{
                minWidth: anchorRef.current?.offsetWidth || 'auto',
                backgroundColor: 'background.default',
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  {otherActions.map(({ menu, form }) => (
                    <MenuItem key={menu.title} disabled={menu.disabled} onClick={() => handleMenuItemClick(form)}>
                      {menu.title}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default MilestonesModalsActionButton;
